// Deterministic ranking + recommendation engine for the FREE BUSINESS DIAG (v0.6).
// Pure functions only: no React, no network, no AI. `answers` is [{questionId, answer}].

export const SCORE = { very_true: 2, somewhat_true: 1, not_really: 0 };

export const CLUSTERS = {
  people: ["q3", "q4", "q7", "q9"],
  systems: ["q5", "q6"],
  profit: ["q2"],
  customers: ["q1"],
};

// §6 — outcome → supporting concerns. Q9 intentionally appears twice. Order is display order.
export const OUTCOMES = [
  { id: "freedom", questions: ["q7", "q8", "q9"] },
  { id: "profit", questions: ["q2"] },
  { id: "team", questions: ["q3", "q4", "q9"] },
  { id: "growth", questions: ["q1", "q6"] },
  { id: "smoother", questions: ["q5"] },
];

const PEOPLE_TIEBREAK = ["q4", "q7", "q3", "q9"];
const SYSTEMS_TIEBREAK = ["q5", "q6"];

const qNum = (id) => parseInt(String(id).slice(1), 10) || 0;
const toMap = (answers) => Object.fromEntries(answers.map((a) => [a.questionId, a.answer]));
const scoreOf = (map, id) => SCORE[map[id]] || 0;

export function qualifyingConcerns(answers) {
  return answers
    .filter((a) => SCORE[a.answer] >= 1)
    .sort((a, b) => qNum(a.questionId) - qNum(b.questionId));
}

export function clusterScore(answers, cluster) {
  const map = toMap(answers);
  return CLUSTERS[cluster].reduce((sum, id) => sum + scoreOf(map, id), 0);
}

/** §5 — which prioritization path to show after Q10. */
export function prioritizationMode(answers) {
  const n = qualifyingConcerns(answers).length;
  if (n === 0) return "none";
  if (n === 1) return "single";
  if (n <= 3) return "simple";
  return "outcomes";
}

/** §6 — outcomes backed by at least one qualifying concern, with those concerns attached. */
export function supportedOutcomes(answers) {
  const map = toMap(answers);
  return OUTCOMES
    .map((o) => ({ id: o.id, questions: o.questions.filter((id) => scoreOf(map, id) >= 1) }))
    .filter((o) => o.questions.length > 0);
}

// Highest score first; ties broken by the order of `candidates`.
function strongest(map, candidates, eligible) {
  return candidates
    .filter((id) => eligible.includes(id))
    .reduce((best, id) => (best === null || scoreOf(map, id) > scoreOf(map, best) ? id : best), null);
}

/**
 * §8 — Jonathan's deterministic recommendation. Only runs on "Help me choose" or Skip.
 * @param teamSize "solo" | "2_5" | "6_15" | "16_plus" | null (null → non-solo path)
 * @param eligibleIds optional restriction (outcome follow-up screen)
 * @returns questionId or null when nothing is eligible
 */
export function recommendConcern(answers, teamSize, eligibleIds) {
  const map = toMap(answers);
  const qualifying = qualifyingConcerns(answers).map((a) => a.questionId);
  const eligible = eligibleIds ? qualifying.filter((id) => eligibleIds.includes(id)) : qualifying;
  if (eligible.length === 0) return null;

  const ok = (id) => eligible.includes(id);
  const veryTrue = (id) => map[id] === "very_true";
  const fallback = () => strongest(map, eligible, eligible);

  if (teamSize === "solo") {
    if (veryTrue("q2") && ok("q2")) return "q2";
    if (veryTrue("q1") && ok("q1")) return "q1";
    if (ok("q7")) return "q7";
    return fallback();
  }

  const people = clusterScore(answers, "people");
  const systems = clusterScore(answers, "systems");

  if (people >= 3) {
    const pick = strongest(map, PEOPLE_TIEBREAK, eligible);
    if (pick) return pick;
  }
  if (systems >= 3 && people <= 2) {
    const pick = strongest(map, SYSTEMS_TIEBREAK, eligible);
    if (pick) return pick;
  }
  if (veryTrue("q2") && people <= 2 && systems <= 2 && ok("q2")) return "q2";
  if (veryTrue("q1") && ok("q1")) return "q1";
  return fallback();
}

/** How the first-ranked result was chosen (drives "Why am I seeing this?" + admin brief). */
export function prioritySource(answers, priority, otherText, accepted) {
  const qualifying = qualifyingConcerns(answers);
  if (qualifying.length === 0) return "none";
  if (qualifying.length === 1) return "single";
  if (priority === "other" && (otherText || "").trim()) return "other";
  if (priority && priority.startsWith("q") && qualifying.some((a) => a.questionId === priority)) {
    return accepted ? "accepted_suggestion" : "explicit";
  }
  return "default";
}

function byIntensityThenOrder(a, b) {
  return SCORE[b.answer] - SCORE[a.answer] || qNum(a.questionId) - qNum(b.questionId);
}

/**
 * §10 — Ranked results (max 3). An explicit/accepted priority or nonblank "other" ranks first;
 * otherwise the deterministic recommendation ranks first. Rest: Very true → Somewhat → order.
 */
export function rankConcerns(answers, priority, otherText, teamSize) {
  const qualifying = qualifyingConcerns(answers);
  if (qualifying.length === 0) return [];
  const asRanked = (a) => ({ questionId: a.questionId, severity: a.answer });
  const trimmedOther = (otherText || "").trim();

  if (priority === "other" && trimmedOther) {
    const rest = [...qualifying].sort(byIntensityThenOrder).slice(0, 2).map(asRanked);
    return [{ questionId: "other", severity: null, otherText: trimmedOther }, ...rest];
  }
  const explicit = priority && qualifying.find((a) => a.questionId === priority);
  const firstId = explicit ? priority : recommendConcern(answers, teamSize);
  const first = qualifying.find((a) => a.questionId === firstId);
  const rest = qualifying.filter((a) => a.questionId !== firstId).sort(byIntensityThenOrder).slice(0, 2);
  return [first, ...rest].map(asRanked);
}

export function selectHeadline(headlines, rankedConcerns) {
  if (!rankedConcerns || rankedConcerns.length === 0) return headlines.none;
  const first = rankedConcerns[0];
  if (first.questionId === "other") return headlines.other;
  return headlines[first.questionId] || headlines.none;
}

export function selectRecommendation(recommendations, concern) {
  if (!concern) return null;
  if (concern.questionId === "other") return recommendations.other || null;
  return recommendations[concern.questionId] || null;
}

/** Solo owners ("Just me") get the solo wording merged over the defaults; teams get defaults. */
export function contentForTeam(content, teamSize) {
  const solo = content?.solo_variants;
  if (!content || teamSize !== "solo" || !solo) return content;
  const mergeEach = (base, patch) =>
    Object.fromEntries(Object.entries(base).map(([k, v]) => [k, patch[k] ? { ...v, ...patch[k] } : v]));
  return {
    ...content,
    headlines: mergeEach(content.headlines, solo.headlines),
    recommendations: mergeEach(content.recommendations, solo.recommendations),
    narrow_screen: {
      ...content.narrow_screen,
      descriptions: { ...content.narrow_screen.descriptions, ...solo.narrow_descriptions },
    },
    outcome_screen: {
      ...content.outcome_screen,
      outcomes: { ...content.outcome_screen.outcomes, ...solo.outcomes },
    },
    question_hints: solo.question_hints,
  };
}

/** Copy v5 display rule: #1 card → Set B ("start here"); others or write-ins → Set A. */
export function jonathanStatement(content, concern, index) {
  const startText = index === 0 ? content.start_here?.[concern.questionId] : null;
  if (startText) return { label: content.jonathan_start_label, text: startText };
  const rec = selectRecommendation(content.recommendations, concern);
  return { label: content.jonathan_take_label, text: rec?.jonathan_take };
}

export function actionLabel(index) {
  return index === 0 ? "Your First Action" : "Try this";
}

const WHY_KEY = {
  none: "no_concerns",
  single: "single_concern",
  other: "with_other",
  explicit: "with_priority",
  accepted_suggestion: "accepted_suggestion",
  default: "skipped_priority",
};

/** "Why am I seeing this?" — only the fragment matching the owner's actual route. */
export function whyAmISeeingThis(whyLib, source) {
  return [whyLib.preamble, whyLib[WHY_KEY[source]]];
}
